import {
  AuthActions,
  ContentActions,
  EMPIRE_NAV_THIN_HEIGHT,
  OpenUrlTarget,
  Selectors,
  useIsDesktop,
  useIsWeb,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useFocusEffect} from '@react-navigation/native';
import {useDispatch, useSelector} from 'Provider';
import envData from 'env.json';
import {View} from 'native-base';
import React, {useCallback, useRef, useState} from 'react';
import {
  Keyboard,
  SafeAreaView,
  TouchableWithoutFeedback,
  UIManager,
} from 'react-native';

import EmpireFooter from 'components/EmpireFooter';

import {StyledScrollView} from 'components/Styled';
import {ViewExpand} from 'components/Views';
import ConnectedHeaderContainer from 'containers/ConnectedHeaderContainer';
import ConnectedMyRewardsFooter from 'containers/ConnectedMyRewardsFooter';
import {isWebViewSelector} from 'store/content';
import {UIActions as MyRewardsUIActions} from 'store/ui/ui.reducer';
import {getLinkData, navigateOrOpenUrl} from 'utils/navigation';

import {FCC} from '../../../types';

const {API_URL} = envData;
interface PageTemplateProps {
  stickyHeaderIndices?: number[];
  headerElements?: React.ReactNode[];
}
enum SCROLL_DIRECTION {
  UP,
  DOWN,
}

export const scrollViewRef = React.createRef<HTMLElement>();

const PageTemplate: FCC<PageTemplateProps> = ({
  children,
  stickyHeaderIndices,
  headerElements,
}) => {
  const widgetDispatch = useWidgetDispatch();
  const suppressHeaderAndFooter = useSelector(isWebViewSelector);
  const isDesktop = useIsDesktop();
  const lastScroll = useRef(0);
  const scrollDirection = useRef(SCROLL_DIRECTION.UP);
  const [isHeaderOpen, setHeaderOpen] = useState(false);
  const atPageBottom = useRef(false);
  const isLoggedIn = useWidgetState(Selectors.isLoggedInSelector);
  const componentLoaded = useRef(false);
  const dispatch = useDispatch();
  const isLogoExpanded = useRef(true);
  const contentRef = useRef(null);
  const setIsLogoExpanded = useCallback(
    (isExpanded: boolean) => {
      dispatch(MyRewardsUIActions.setIsLogoExpanded(isExpanded));
    },
    [dispatch]
  );
  const isWeb = useIsWeb();

  useFocusEffect(
    useCallback(() => {
      // Only set the UIManager.focus for web
      if (!isWeb) {
        return;
      }
      if (!componentLoaded.current) {
        componentLoaded.current = true;
        // Set tab focus on the page content
        UIManager.focus(contentRef.current);
      }

      // Reset flag so next time we come back also focuses top of page
      return () => {
        componentLoaded.current = false;
      };
    }, [])
  );

  const handleOnScroll = ({nativeEvent}) => {
    const y = nativeEvent.contentOffset.y;
    const maxY =
      nativeEvent.contentSize.height - nativeEvent.layoutMeasurement.height;

    // prevent the bouncing header on desktop
    if (maxY - y < EMPIRE_NAV_THIN_HEIGHT + 1) {
      atPageBottom.current = true;
    } else {
      atPageBottom.current = false;
    }

    // Safari has ability to report contentOffset as negative when bouncing.
    // When bouncing, force last position and scroll direction
    if (y < 0) {
      if (scrollDirection.current === SCROLL_DIRECTION.UP) {
        return;
      }

      lastScroll.current = 0;
      scrollDirection.current = SCROLL_DIRECTION.UP;

      if (isLogoExpanded.current !== !atPageBottom.current) {
        setIsLogoExpanded(!atPageBottom.current);
        isLogoExpanded.current = !atPageBottom.current;
      }
      return;
    }

    // Also handle bottom of page for safari
    if (y > maxY) {
      if (scrollDirection.current === SCROLL_DIRECTION.DOWN) {
        return;
      }

      lastScroll.current = maxY;
      scrollDirection.current = SCROLL_DIRECTION.DOWN;

      if (isLogoExpanded.current) {
        setIsLogoExpanded(false);
        isLogoExpanded.current = false;
      }
      return;
    }

    if (isWeb) {
      window.scrollViewY = y;
    }

    if (y === lastScroll.current) {
      // We are done scrolling
      return;
    }

    const direction =
      y > lastScroll.current ? SCROLL_DIRECTION.DOWN : SCROLL_DIRECTION.UP;
    lastScroll.current = y;
    scrollDirection.current = direction;
    const shouldExpandLogo =
      !atPageBottom.current && scrollDirection.current === SCROLL_DIRECTION.UP;

    if (isLogoExpanded.current !== shouldExpandLogo) {
      setIsLogoExpanded(shouldExpandLogo);
      isLogoExpanded.current = shouldExpandLogo;
    }
  };

  const userDetailMenuCollapsed = useWidgetState(
    Selectors.empireNavigationUserDetailMenuCollapsedSelector
  ) as boolean | undefined;

  const handleCloseUserDetailMenu = useCallback(
    e => {
      const userDetailMenu = document.getElementById('user-detail-menu');
      const isFound = userDetailMenu?.contains(e.target);
      if (!isFound) {
        if (userDetailMenuCollapsed !== undefined && !userDetailMenuCollapsed) {
          widgetDispatch(ContentActions.setUserDetailMenuCollapsed(true));
        }
      }
    },
    [widgetDispatch, userDetailMenuCollapsed]
  );

  const handleFooterItemPress = (
    title?: string,
    path?: string,
    openNewTab?: boolean
  ) => {
    const linkData = getLinkData(title || '', path || '');
    const {newRewardsScreen, url} = linkData;
    const prefix = url.startsWith('/') ? API_URL : '';
    const legacyUrl = `${prefix}${url}`;
    const isInMyRewards = legacyUrl?.includes('/myrewards/profile');
    const target =
      !isInMyRewards && openNewTab ? OpenUrlTarget.BLANK : OpenUrlTarget.SELF;

    navigateOrOpenUrl({
      legacyUrl,
      newRewardsScreen,
      target,
    });
  };

  const getFooterItemHref = (title?: string, path?: string) => {
    const {analyticsHref} = getLinkData(title || '', path || '');
    return analyticsHref;
  };

  const touchWrapper = (wrapperChildren: React.ReactNode) =>
    isWeb ? (
      <View style={{outlineStyle: 'none'}} ref={contentRef}>
        {wrapperChildren}
      </View>
    ) : (
      <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
        <View ref={contentRef}>{wrapperChildren}</View>
      </TouchableWithoutFeedback>
    );

  const onRefreshSession = () => {
    if (isWeb && isLoggedIn) {
      widgetDispatch(AuthActions.refreshUserSession());
    }
  };

  return (
    <ViewExpand
      testID="Main container"
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      onKeyDown={onRefreshSession}
      onStartShouldSetResponderCapture={e => {
        if (isWeb) {
          handleCloseUserDetailMenu(e);
          onRefreshSession();
        }
        return false;
      }}>
      <View style={{flex: 1}}>
        {!suppressHeaderAndFooter && (
          <ConnectedHeaderContainer onOpen={setHeaderOpen} />
        )}
        <StyledScrollView
          ref={scrollViewRef}
          display={isHeaderOpen ? 'none' : 'flex'}
          onScroll={handleOnScroll}
          scrollEventThrottle={64}
          stickyHeaderIndices={stickyHeaderIndices}>
          {headerElements}
          {touchWrapper(children)}
          {!suppressHeaderAndFooter && (
            <View mt={isDesktop ? 8 : 6}>
              <ConnectedMyRewardsFooter
                onFooterItemPress={handleFooterItemPress}
                footerItemHref={getFooterItemHref}
              />
              <EmpireFooter />
            </View>
          )}
        </StyledScrollView>
      </View>
    </ViewExpand>
  );
};

export default PageTemplate;
